import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tag, Upload, Check, X, Loader2, Square, Circle, RectangleHorizontal } from "lucide-react";
import { GuidelinesLink } from "@/components/GuidelinesLink";

const SHAPE_ICON = { Rectangle: RectangleHorizontal, "Rounded Rectangle": RectangleHorizontal, Square: Square, Circle: Circle, Oval: Circle };

function labelAspect(size) {
  const nums = (size || "").match(/[\d.]+/g);
  if (!nums || nums.length < 2) return 1;
  return (parseFloat(nums[0]) || 1) / (parseFloat(nums[1]) || 1);
}
function labelSkin(shape, material) {
  const clear = /clear|transparent/i.test(material || "");
  const bg = /kraft/i.test(material || "") ? "#d9c3a0" : clear ? "transparent" : "#ffffff";
  let radius = "8px";
  if (shape === "Circle" || shape === "Oval") radius = "50%";
  else if (shape === "Rounded Rectangle") radius = "16%";
  return { bg, radius, clear };
}

export function colorSwatchStyle(c) {
  if (!c) return { style: {}, checker: false };
  const base = c.swatch || "";
  const clear = !base || base === "transparent" || /clear|transparent/i.test(c.name || "");
  if (clear) return { style: { background: "transparent" }, checker: true };
  if (c.finish === "hologram") {
    return { style: { backgroundImage: `linear-gradient(135deg, ${base}dd, ${base}55), conic-gradient(from 45deg, #ff5cc4, #22c3d6, #4fd23a, #ffd23f, #ff6a13, #ff5cc4)`, backgroundBlendMode: "screen" }, checker: false };
  }
  if (c.finish === "glossy") {
    return { style: { backgroundImage: `linear-gradient(135deg, rgba(255,255,255,.85), rgba(255,255,255,0) 45%), linear-gradient(${base}, ${base})` }, checker: false };
  }
  return { style: { background: base }, checker: false };
}

function unitFor(style, mode, qty) {
  let b = style.price, c = style.color_price != null ? style.color_price : style.price;
  (style.tiers || []).slice().sort((x, y) => (x.min_qty || 0) - (y.min_qty || 0)).forEach((t) => {
    if (qty >= (t.min_qty || 0)) { if (t.price != null) b = t.price; if (t.color_price != null) c = t.color_price; }
  });
  return mode === "color" ? c : b;
}

export default function OrderLabels() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [styles, setStyles] = useState([]);
  const [styleId, setStyleId] = useState("");
  const [mode, setMode] = useState("black");
  const [qty, setQty] = useState(50);
  const [artMode, setArtMode] = useState("upload");
  const [catalog, setCatalog] = useState([]);
  const [artwork, setArtwork] = useState("");
  const [details, setDetails] = useState("");
  const [uploading, setUploading] = useState(false);
  const [rush, setRush] = useState(false);
  const [rushCfg, setRushCfg] = useState({ enabled: false, pct: 0, days: "" });
  const [colors, setColors] = useState([]);
  const [colorId, setColorId] = useState("");

  useEffect(() => { api.get("/label-styles").then((r) => setStyles(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/label-rush").then((r) => setRushCfg(r.data || { enabled: false })).catch(() => {}); }, []);
  useEffect(() => { api.get("/label-colors").then((r) => { setColors(r.data || []); if ((r.data || []).length && !colorId) setColorId(r.data[0].color_id); }).catch(() => {}); }, []); // eslint-disable-line
  useEffect(() => { if (artMode === "catalog" && catalog.length === 0) api.get("/designs").then((r) => setCatalog(r.data || [])).catch(() => {}); }, [artMode]); // eslint-disable-line

  const style = styles.find((s) => s.style_id === styleId);
  const sortedTiers = useMemo(() => (style?.tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty), [style]);
  const selColor = colors.find((c) => c.color_id === colorId);
  const colorUp = selColor ? (selColor.upcharge || 0) : 0;
  const q = Math.max(1, parseInt(qty) || 1);
  const unit = (style ? unitFor(style, mode, q) : 0) + colorUp;
  const rushActive = rush && rushCfg.enabled;
  const rushFee = rushActive ? (rushCfg.fee || 0) : 0;
  const lineTotal = unit * q + rushFee;

  const onUpload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setArtwork(data.url);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const addToCart = () => {
    if (!style) { toast.error("Choose a label style"); return; }
    if (!artwork && !details.trim()) { toast.error("Upload your artwork or describe what you need"); return; }
    addItem({
      kind: "label",
      name: `Labels — ${style.name}${selColor ? ` · ${selColor.name}` : ""} (${mode === "color" ? "Colored" : "Black"})${rushActive ? " · Rush" : ""}`,
      unit_price: unit, quantity: q,
      style_id: style.style_id, style_name: style.name, shape: style.shape, size: style.size,
      material: style.material, print_mode: mode, rush: rushActive,
      color_id: selColor ? selColor.color_id : null, color_name: selColor ? selColor.name : null,
      finish: selColor ? selColor.finish : null, color_swatch: selColor ? selColor.swatch : null,
      color_upcharge: colorUp,
      artwork_url: artwork || null, details: details.trim() || null,
      preview_url: artwork || null, image_url: artwork || null,
    });
    toast.success("Labels added to cart");
    navigate("/cart");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="order-labels-page">
      <div className="flex items-center gap-2 text-[#ff6a13] font-mono-p text-xs uppercase tracking-widest"><Tag size={14} />Custom Labels</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-2">Order Labels & Stickers</h1>
      <p className="text-zinc-400 mt-2 max-w-2xl">Pick a size & shape, choose black or colored printing, upload your logo or finished label, and set your quantity. Price updates live with bulk discounts.</p>

      {/* Styles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
        {styles.map((s) => {
          const Icon = SHAPE_ICON[s.shape] || Square;
          const active = styleId === s.style_id;
          return (
            <button key={s.style_id} onClick={() => setStyleId(s.style_id)} data-testid={`label-style-${s.style_id}`}
              className={`text-left rounded-2xl p-4 border transition-all ${active ? "border-[#ff6a13] bg-[#161922] ring-1 ring-[#ff6a13]" : "border-zinc-800 bg-[#12141a] hover:border-zinc-700"}`}>
              <div className="flex items-center justify-between">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-zinc-800 text-[#22c3d6]"><Icon size={20} className={s.shape === "Oval" ? "scale-x-150" : ""} /></span>
                {active && <Check size={18} className="text-[#ff6a13]" />}
              </div>
              <p className="font-semibold text-white mt-3 leading-tight">{s.name}</p>
              <p className="text-[12px] text-zinc-400 mt-2 font-mono-p">Black ${s.price?.toFixed(2)} · Color ${(s.color_price ?? s.price)?.toFixed(2)}</p>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-4">
          {/* Color & finish */}
          {colors.length > 0 && (
            <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5" data-testid="label-color-card">
              <h2 className="font-display font-bold text-white">Label color &amp; finish</h2>
              <p className="text-xs text-zinc-500 mt-1">Choose your stock color/finish. Some finishes add a small per-label upcharge.</p>
              <div className="flex flex-wrap gap-3 mt-4">
                {colors.map((c) => {
                  const cs = colorSwatchStyle(c);
                  const on = colorId === c.color_id;
                  return (
                    <button key={c.color_id} onClick={() => setColorId(c.color_id)} data-testid={`label-color-${c.color_id}`}
                      className={`flex flex-col items-center gap-1 w-20 rounded-xl p-2 border transition-all ${on ? "border-[#ff6a13] bg-[#161922] ring-1 ring-[#ff6a13]" : "border-zinc-800 hover:border-zinc-700"}`}>
                      <span className={`w-9 h-9 rounded-full ${cs.checker ? "checker" : ""}`} style={{ ...cs.style, boxShadow: "0 0 0 1px rgba(255,255,255,0.25)" }} />
                      <span className="text-[10px] text-zinc-300 leading-tight text-center">{c.name}</span>
                      {c.upcharge > 0 && <span className="text-[9px] text-[#22c3d6] font-mono-p">+${c.upcharge.toFixed(2)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Print + qty */}
          <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
            <h2 className="font-display font-bold text-white">Print & quantity</h2>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div>
                <p className="text-[11px] text-zinc-500 font-mono-p uppercase tracking-wider mb-1">Print</p>
                <div className="flex rounded-full bg-[#1e222d] p-0.5 text-sm">
                  <button onClick={() => setMode("black")} data-testid="print-black" className={`px-4 py-1.5 rounded-full font-semibold ${mode === "black" ? "bg-white text-black" : "text-zinc-400"}`}>Black</button>
                  <button onClick={() => setMode("color")} data-testid="print-color" className={`px-4 py-1.5 rounded-full font-semibold ${mode === "color" ? "bg-[#ff6a13] text-white" : "text-zinc-400"}`}>Colored</button>
                </div>
              </div>
              <div>
                <p className="text-[11px] text-zinc-500 font-mono-p uppercase tracking-wider mb-1">Quantity</p>
                <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="label-qty" className="w-32 bg-[#1e222d] border-zinc-700 text-white" />
              </div>
              {style && (
                <div className="ml-auto text-right">
                  <p className="text-[11px] text-zinc-500 font-mono-p">${unit.toFixed(2)} / label</p>
                  <p className="font-mono-p font-bold text-white text-lg">${lineTotal.toFixed(2)}</p>
                </div>
              )}
            </div>
            {rushCfg.enabled && (
              <label className="flex items-center gap-2 mt-4 cursor-pointer bg-[#1e222d] border border-zinc-800 rounded-lg px-3 py-2" data-testid="rush-toggle">
                <input type="checkbox" checked={rush} onChange={(e) => setRush(e.target.checked)} className="accent-[#ff6a13] w-4 h-4" />
                <span className="text-sm text-zinc-200">Rush turnaround <span className="font-semibold text-[#ff6a13]">+${(rushCfg.fee || 0).toFixed(2)} / order</span></span>
                <span className="text-[11px] text-zinc-500 ml-auto">{rushCfg.days}</span>
              </label>
            )}
            {style && (style.tiers || []).length > 0 && (
              <p className="text-[11px] text-zinc-500 mt-3 font-mono-p">Bulk pricing: {sortedTiers.map((t) => `${t.min_qty}+ = $${(mode === "color" ? (t.color_price ?? t.price) : t.price)?.toFixed(2)}`).join("  ·  ")}</p>
            )}
          </div>

          {/* Artwork */}
          <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-white">Your artwork</h2>
              <div className="flex rounded-full bg-[#1e222d] p-0.5 text-xs">
                <button onClick={() => setArtMode("upload")} data-testid="art-upload" className={`px-3 py-1.5 rounded-full font-semibold ${artMode === "upload" ? "bg-[#ff6a13] text-white" : "text-zinc-400"}`}>Upload</button>
                <button onClick={() => setArtMode("catalog")} data-testid="art-catalog" className={`px-3 py-1.5 rounded-full font-semibold ${artMode === "catalog" ? "bg-[#ff6a13] text-white" : "text-zinc-400"}`}>From catalog</button>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-1">Upload your logo or a finished label (PNG / JPG / PDF), pick from the catalog, or just describe it below. <GuidelinesLink testid="labels-guidelines-link" /></p>

            {artMode === "upload" ? (
              <button onClick={() => fileRef.current?.click()} data-testid="label-upload-drop" className="mt-4 w-full border-2 border-dashed border-zinc-700 rounded-xl py-8 flex flex-col items-center justify-center text-zinc-400 hover:border-[#ff6a13] hover:text-white transition-colors">
                {uploading ? <Loader2 className="animate-spin" size={22} /> : <Upload size={22} />}
                <span className="text-sm mt-2 font-semibold">{uploading ? "Uploading…" : "Click to upload PNG / JPG / PDF"}</span>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,application/pdf" className="hidden" onChange={onUpload} data-testid="label-upload-input" />
              </button>
            ) : (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-56 overflow-auto">
                {catalog.length === 0 && <p className="col-span-full text-sm text-zinc-500 py-6 text-center">No catalog designs available.</p>}
                {catalog.map((g) => (
                  <button key={g.design_id} onClick={() => setArtwork(g.image_url)} data-testid={`label-catalog-${g.design_id}`} title={g.title} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${artwork === g.image_url ? "border-[#ff6a13]" : "border-transparent"} checker`}>
                    <img src={fileUrl(g.image_url)} alt={g.title} className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}

            {artwork && (
              <div className="mt-3 flex items-center gap-3" data-testid="label-artwork-preview">
                <div className="w-16 h-16 rounded-lg overflow-hidden border border-[#22c3d6] checker flex items-center justify-center">
                  <img src={fileUrl(artwork)} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
                <span className="text-sm text-zinc-300">Artwork attached</span>
                <button onClick={() => setArtwork("")} className="text-zinc-500 hover:text-red-400"><X size={16} /></button>
              </div>
            )}

            <Textarea value={details} onChange={(e) => setDetails(e.target.value)} data-testid="label-details" rows={3} placeholder="Optional: notes, colors, text, or describe a label for us to design…" className="mt-3 bg-[#1e222d] border-zinc-700 text-white" />
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[#12141a] border border-zinc-800 rounded-2xl p-6 h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-lg font-bold text-white">Summary</h2>
          {style && (() => { const sk = labelSkin(style.shape, style.material); const cs = selColor ? colorSwatchStyle(selColor) : null; const ar = labelAspect(style.size); const useClear = cs ? cs.checker : sk.clear; return (
            <div className="my-4" data-testid="label-proof-preview">
              <p className="text-[11px] text-zinc-500 font-mono-p uppercase tracking-wider mb-2">Proof preview</p>
              <div className="flex items-center justify-center bg-[#0c0d10] border border-zinc-800 rounded-xl p-5">
                <div className={useClear ? "checker" : ""} style={{ aspectRatio: String(ar), width: ar >= 1 ? "180px" : "auto", height: ar < 1 ? "150px" : "auto", maxWidth: "200px", maxHeight: "170px", ...(useClear ? {} : (cs ? cs.style : { background: sk.bg })), borderRadius: sk.radius, boxShadow: "0 0 0 1px rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: "10px" }}>
                  {artwork ? <img src={fileUrl(artwork)} alt="proof" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} /> : <span className="text-[10px] text-zinc-500 text-center px-2">Your artwork here</span>}
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 text-center mt-1 font-mono-p">{style.shape} · {style.size}{selColor ? ` · ${selColor.name}` : ` · ${style.material}`}</p>
            </div>
          ); })()}
          <div className="flex justify-between text-sm mt-4"><span className="text-zinc-400">Style</span><span className="font-mono-p text-white text-right">{style ? style.name : "—"}</span></div>
          {selColor && <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Color</span><span className="font-mono-p text-white text-right capitalize">{selColor.name} · {selColor.finish}</span></div>}
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Print</span><span className="font-mono-p text-white">{mode === "color" ? "Colored" : "Black"}</span></div>
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Quantity</span><span className="font-mono-p text-white">{q}</span></div>
          {colorUp > 0 && <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Color upcharge</span><span className="font-mono-p text-white">+${colorUp.toFixed(2)}/label</span></div>}
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Per label</span><span className="font-mono-p text-white">${unit.toFixed(2)}</span></div>
          {rushActive && <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Rush fee</span><span className="font-mono-p text-white">${rushFee.toFixed(2)}</span></div>}
          <div className="border-t border-zinc-800 my-4" />
          <div className="flex justify-between items-center"><span className="text-zinc-300 font-semibold">Total</span><span className="font-mono-p font-bold text-2xl text-white">${lineTotal.toFixed(2)}</span></div>
          <Button onClick={addToCart} data-testid="label-add-to-cart" className="w-full mt-5 bg-[#ff6a13] hover:bg-[#ff8a3d] text-white rounded-full h-11 font-semibold">Add to Cart</Button>
        </div>
      </div>
    </div>
  );
}
